import { Component, EventEmitter, Output, Input, OnInit, OnChanges, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { SafeHtml, DomSanitizer } from "@angular/platform-browser";
import { AppText, Languages } from "src/app/interfaces/common";
import { AppLoadService } from "src/app/services/app-load.service";
import { environment } from "src/environments/environment";
import { Observable, of, from, Subject } from "rxjs";
import { catchError, switchMap, map, takeUntil } from "rxjs/operators";

@Component({
  selector: "app-about",
  templateUrl: "./about.component.html",
  styleUrls: ["./about.component.css"],
})
export class AboutComponent implements OnInit, OnChanges, OnDestroy {
  @Output() closeAboutee = new EventEmitter<void>();
  @Input() selText: AppText = {};
  @Input() applicationSettings: any;
  @Input() lang: Languages = Languages.Icelandic;
  @Input() rawHtml: string = "";

  currentYear: number | null = new Date().getFullYear();
  showCookiePolicy = false;
  safeHtml: SafeHtml = "";
  frontendVersionNumber = environment.clientVersion;
  backendVersionNumber = environment.serverVersion;
  authGroupInfo: any;
  ownerTitle = "";
  isChangelogVisible = false;
  changelogUrl = "assets/CHANGELOG.md";
  changelogContent: Observable<string> = of(""); // Will contain sanitized HTML changelog

  private destroy$ = new Subject<void>();

  constructor(
    private appLoadService: AppLoadService,
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.authGroupInfo = this.appLoadService.getAuthGroupInfo();
    this.updateOwnerTitle();

    if (this.applicationSettings.option?.appInfo?.copyright?.skipCurrentYear) {
      this.currentYear = null;
    }

    this.validateChangelogUrl();

    this.changelogContent = this.http
      .get(this.changelogUrl, { responseType: "text" })
      .pipe(
        switchMap((content) =>
          from(
            Promise.all([
              import("marked"),
              import("dompurify"),
            ])
          ).pipe(
            map(([{ marked }, DOMPurifyModule]) => {
              marked.setOptions({ silent: true, breaks: true });
              const rawHtml = marked.parse(content) as string;
              const safeHtml = DOMPurifyModule.default.sanitize(
                rawHtml,
                this.getDomPurifyConfig()
              );
              return safeHtml;
            }),
            catchError((err) => {
              console.error("Error loading markdown parser or sanitizer:", err);
              return of("Unable to render changelog content");
            })
          )
        ),
        catchError((error) => {
          console.error("Failed to load changelog:", error);
          return of("Unable to load changelog content");
        })
      );
  }

  ngOnChanges(): void {
    if (this.rawHtml) {
      from(import("dompurify"))
        .pipe(
          takeUntil(this.destroy$),
          map((DOMPurifyModule) =>
            DOMPurifyModule.default.sanitize(this.rawHtml, this.getDomPurifyConfig())
          ),
          catchError((err) => {
            console.error("Failed to sanitize rawHtml:", err);
            return of("");
          })
        )
        .subscribe((cleanHtml) => {
          this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(cleanHtml);
        });
    }
    this.updateOwnerTitle();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeAbout(): void {
    this.closeAboutee.emit();
  }

  private updateOwnerTitle(): void {
    if (this.lang === Languages.Icelandic && this.authGroupInfo?.group?.option?.title?.is) {
      this.ownerTitle = this.authGroupInfo.group.option.title.is;
    } else if (this.lang === Languages.English && this.authGroupInfo?.group?.option?.title?.en) {
      this.ownerTitle = this.authGroupInfo.group.option.title.en;
    } else {
      this.ownerTitle = this.selText.name;
    }
  }

  openChangelog(): void {
    this.isChangelogVisible = true;
  }

  closeChangelog(): void {
    this.isChangelogVisible = false;
  }

  // Get DOMPurify configuration for sanitization
  private getDomPurifyConfig() {
    return {
      FORBID_TAGS: ["style", "script", "iframe", "form"],
      FORBID_ATTR: ["onerror", "onclick", "onload", "onmouseover"],
      ADD_ATTR: ["target", "rel"],
    };
  }

  // Validate changelog URL to prevent directory traversal or external injection
  private validateChangelogUrl(): void {
    if (!/^assets\/[a-zA-Z0-9._/-]+$/.test(this.changelogUrl)) {
      this.changelogUrl = "assets/CHANGELOG.md";
      console.warn("Changelog URL was reset for security reasons");
    }
  }
}
